import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Flex,
  Spinner,
  Text,
  Box,
  HStack,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  FileX,
  Receipt,
  Warning,
  Buildings,
  CurrencyDollarSimple,
} from 'phosphor-react';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useDebounceCallback } from 'usehooks-ts';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { IData, IDataNoPagination } from '../../../../types/shared';
import {
  IAuditoriaFiscal,
  IAuditoriaFiscalKpi,
  useAuditoriaFiscal,
} from '../../../../hooks/useAuditoriaFiscal';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { PageBox } from '../../../../components/pageBox';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';

moment.locale('pt-br');

interface AuditoriaCardProps {
  auditoria: IAuditoriaFiscal;
}

function AuditoriaCard({ auditoria }: AuditoriaCardProps) {
  const hasErros =
    auditoria.pis_erro ||
    auditoria.cofins_erro ||
    auditoria.retencao_erro ||
    auditoria.mensagem_erro;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'OK':
        return (
          <Badge colorScheme="green" display="flex" alignItems="center" gap={1}>
            <CheckCircle size={12} weight="fill" />
            OK
          </Badge>
        );
      case 'ERRO':
        return (
          <Badge colorScheme="red" display="flex" alignItems="center" gap={1}>
            <XCircle size={12} weight="fill" />
            ERRO
          </Badge>
        );
      case 'FATURA':
        return (
          <Badge colorScheme="purple" display="flex" alignItems="center" gap={1}>
            <Receipt size={12} weight="fill" />
            FATURA
          </Badge>
        );
      case 'SEM_PASTA':
        return (
          <Badge colorScheme="gray" display="flex" alignItems="center" gap={1}>
            <FileX size={12} weight="fill" />
            SEM PASTA
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getBorderColor = () => {
    switch (auditoria.status) {
      case 'OK':
        return 'green.300';
      case 'ERRO':
        return 'red.300';
      case 'FATURA':
        return 'purple.300';
      case 'SEM_PASTA':
        return 'gray.300';
      default:
        return 'gray.200';
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      borderLeftWidth="4px"
      borderLeftColor={getBorderColor()}
      p={3}
      transition="all 0.2s"
      _hover={{
        boxShadow: 'sm',
      }}
      minW={0}
      w="100%"
    >
      {/* Header com ID e Status */}
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="xs" fontWeight="600" color="gray.500">
          ID: {auditoria.codi_emp}
        </Text>
        {getStatusBadge(auditoria.status)}
      </Flex>

      {/* Nome da Empresa */}
      <Flex align="center" gap={1.5} mb={2} minW={0}>
        <Icon as={Buildings} color="blue.500" boxSize={4} flexShrink={0} />
        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={2} minW={0}>
          {auditoria.nome_empresa}
        </Text>
      </Flex>

      {/* Faturamento */}
      {auditoria.faturamento !== null && auditoria.faturamento !== undefined && (
        <Box bg="blue.50" px={2} py={1.5} borderRadius="md" mb={2}>
          <Flex align="center" gap={1.5}>
            <Icon as={CurrencyDollarSimple} color="blue.500" boxSize={4} flexShrink={0} />
            <VStack align="flex-start" spacing={0} flex={1} minW={0}>
              <Text fontSize="2xs" color="blue.600" fontWeight="500">
                Faturamento
              </Text>
              <Text fontSize="sm" fontWeight="700" color="blue.700">
                {formatCurrency(auditoria.faturamento)}
              </Text>
            </VStack>
          </Flex>
        </Box>
      )}

      {/* Valores OK - mostra apenas se tiver algum valor */}
      {auditoria.status === 'OK' &&
        (auditoria.pis_pdf !== null ||
          auditoria.cofins_pdf !== null ||
          auditoria.retencao_pdf !== null) && (
          <Box mt={2} pt={2} borderTop="1px" borderColor="gray.100">
            <Text
              fontSize="2xs"
              fontWeight="600"
              color="green.600"
              mb={1.5}
              textTransform="uppercase"
            >
              Valores Encontrados
            </Text>
            <VStack spacing={1.5} align="stretch">
              {auditoria.pis_pdf !== null && (
                <Box bg="green.50" p={2} borderRadius="md">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="2xs" fontWeight="bold" color="green.700">
                      PIS
                    </Text>
                    <Text fontSize="xs" fontWeight="600" color="green.700">
                      {formatCurrency(auditoria.pis_pdf)}
                    </Text>
                  </Flex>
                </Box>
              )}
              {auditoria.cofins_pdf !== null && (
                <Box bg="green.50" p={2} borderRadius="md">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="2xs" fontWeight="bold" color="green.700">
                      COFINS
                    </Text>
                    <Text fontSize="xs" fontWeight="600" color="green.700">
                      {formatCurrency(auditoria.cofins_pdf)}
                    </Text>
                  </Flex>
                </Box>
              )}
              {auditoria.retencao_pdf !== null && (
                <Box bg="green.50" p={2} borderRadius="md">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="2xs" fontWeight="bold" color="green.700">
                      RETENÇÃO
                    </Text>
                    <Text fontSize="xs" fontWeight="600" color="green.700">
                      {formatCurrency(auditoria.retencao_pdf)}
                    </Text>
                  </Flex>
                </Box>
              )}
            </VStack>
          </Box>
        )}

      {/* Erros */}
      {hasErros && auditoria.status !== 'OK' && (
        <Box mt={2} pt={2} borderTop="1px" borderColor="gray.100">
          <Text fontSize="2xs" fontWeight="600" color="red.600" mb={1.5} textTransform="uppercase">
            Detalhes do Erro
          </Text>
          <VStack spacing={1.5} align="stretch">
            {auditoria.pis_erro && (
              <Box bg="red.50" p={2} borderRadius="md">
                <Text fontSize="2xs" fontWeight="bold" color="red.700" mb={0.5}>
                  PIS
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {auditoria.pis_erro}
                </Text>
                {(auditoria.pis_pdf !== null || auditoria.pis_calculo !== null) && (
                  <Flex gap={2} mt={1} flexWrap="wrap">
                    <Text fontSize="2xs" color="gray.600">
                      PDF: {formatCurrency(auditoria.pis_pdf)}
                    </Text>
                    <Text fontSize="2xs" color="gray.600">
                      Calc: {formatCurrency(auditoria.pis_calculo)}
                    </Text>
                    <Text fontSize="2xs" color="gray.600">
                      Manual: {formatCurrency(auditoria.pis_calc_manual)}
                    </Text>
                  </Flex>
                )}
              </Box>
            )}
            {auditoria.cofins_erro && (
              <Box bg="red.50" p={2} borderRadius="md">
                <Text fontSize="2xs" fontWeight="bold" color="red.700" mb={0.5}>
                  COFINS
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {auditoria.cofins_erro}
                </Text>
                {(auditoria.cofins_pdf !== null || auditoria.cofins_calculo !== null) && (
                  <Flex gap={2} mt={1} flexWrap="wrap">
                    <Text fontSize="2xs" color="gray.600">
                      PDF: {formatCurrency(auditoria.cofins_pdf)}
                    </Text>
                    <Text fontSize="2xs" color="gray.600">
                      Calc: {formatCurrency(auditoria.cofins_calculo)}
                    </Text>
                    <Text fontSize="2xs" color="gray.600">
                      Manual: {formatCurrency(auditoria.cofins_calc_manual)}
                    </Text>
                  </Flex>
                )}
              </Box>
            )}
            {auditoria.retencao_erro && (
              <Box bg="red.50" p={2} borderRadius="md">
                <Text fontSize="2xs" fontWeight="bold" color="red.700" mb={0.5}>
                  RETENCAO
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {auditoria.retencao_erro}
                </Text>
              </Box>
            )}
            {auditoria.mensagem_erro && !auditoria.pis_erro && !auditoria.cofins_erro && (
              <Box bg="orange.50" p={2} borderRadius="md">
                <Text fontSize="xs" color="gray.700">
                  {auditoria.mensagem_erro}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

interface SummaryCardsProps {
  kpi: IAuditoriaFiscalKpi;
  isLoading: boolean;
  statusFilter: string;
  // eslint-disable-next-line no-unused-vars
  onStatusFilterChange: (status: string) => void;
}

function SummaryCards({ kpi, isLoading, statusFilter, onStatusFilterChange }: SummaryCardsProps) {
  return (
    <Box mb={6}>
      <SimpleGrid columns={{ base: 2, xl: 5 }} spacing={{ base: 2, md: 4 }}>
        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => onStatusFilterChange('')}
          border={statusFilter === '' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === '' ? 'blue.400' : 'gray.200'}
          _hover={{ borderColor: 'blue.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#E3F2FD" flexShrink={0}>
                <HiOutlineOfficeBuilding color="#1976D2" size={18} />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Total
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.total}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => onStatusFilterChange(statusFilter === 'OK' ? '' : 'OK')}
          border={statusFilter === 'OK' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'OK' ? 'green.400' : 'gray.200'}
          _hover={{ borderColor: 'green.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#E8F5E9" flexShrink={0}>
                <CheckCircle color="#4CAF50" size={18} weight="fill" />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  OK
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.ok}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => onStatusFilterChange(statusFilter === 'ERRO' ? '' : 'ERRO')}
          border={statusFilter === 'ERRO' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'ERRO' ? 'red.400' : 'gray.200'}
          _hover={{ borderColor: 'red.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#FFEBEE" flexShrink={0}>
                <XCircle color="#E53935" size={18} weight="fill" />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Erro
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.erro}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => onStatusFilterChange(statusFilter === 'FATURA' ? '' : 'FATURA')}
          border={statusFilter === 'FATURA' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'FATURA' ? 'purple.400' : 'gray.200'}
          _hover={{ borderColor: 'purple.300' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#F3E5F5" flexShrink={0}>
                <Receipt color="#8E24AA" size={18} weight="fill" />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Fatura
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.fatura}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>

        <Card
          shadow="sm"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => onStatusFilterChange(statusFilter === 'SEM_PASTA' ? '' : 'SEM_PASTA')}
          border={statusFilter === 'SEM_PASTA' ? '2px solid' : '1px solid'}
          borderColor={statusFilter === 'SEM_PASTA' ? 'gray.500' : 'gray.200'}
          _hover={{ borderColor: 'gray.400' }}
          transition="all 0.2s"
        >
          <CardBody py={{ base: 2, md: 3 }} px={{ base: 2, md: 4 }}>
            <Flex align="center" gap={{ base: 2, md: 3 }}>
              <Box p={{ base: 1.5, md: 2 }} borderRadius="lg" bg="#ECEFF1" flexShrink={0}>
                <FileX color="#546E7A" size={18} weight="fill" />
              </Box>
              <Box minW={0} flex={1}>
                <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5} noOfLines={1}>
                  Sem Pasta
                </Text>
                {isLoading ? (
                  <Flex align="center" h="24px">
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Text fontSize={{ base: 'sm', xl: 'lg' }} fontWeight="bold" color="gray.800">
                    {kpi.sem_pasta}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  );
}

export function ModulesHomeFiscalAuditoriaPage() {
  const { getAuditorias, getAuditoriasKpi } = useAuditoriaFiscal();

  const {
    currentDate: currentFilterDate,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({
    defaultToPreviousMonth: true,
  });

  const [searchMode, setSearchMode] = useState<'Nome' | 'ID' | 'CNPJ'>('Nome');
  const [searchText, setSearchText] = useState(
    () => filters.get('search') || filters.get('external_id') || filters.get('cnpj') || '',
  );
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [allAuditorias, setAllAuditorias] = useState<IAuditoriaFiscal[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const previousFilterKeyRef = useRef<string>('');
  const isLoadingMoreRef = useRef(false);

  const debouncedSearch = useDebounceCallback((value: string) => {
    const newFilters = new URLSearchParams(filters);
    newFilters.delete('search');
    newFilters.delete('external_id');
    newFilters.delete('cnpj');
    if (value) {
      if (searchMode === 'ID') {
        newFilters.set('external_id', value);
      } else if (searchMode === 'CNPJ') {
        newFilters.set('cnpj', value.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      } else {
        newFilters.set('search', value);
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    const newFilters = new URLSearchParams(filters);
    if (status) {
      newFilters.set('status', status);
    } else {
      newFilters.delete('status');
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  };

  // Criar chave de filtros para detectar mudanças
  const filterKey = useMemo(() => {
    const filtersObj = Object.fromEntries([...Array.from(filters)]);
    delete filtersObj.page;
    return JSON.stringify(filtersObj);
  }, [filters]);

  // Resetar quando filtros mudam
  useEffect(() => {
    if (previousFilterKeyRef.current && previousFilterKeyRef.current !== filterKey) {
      setAllAuditorias([]);
      setCurrentPage(1);
      setHasMore(true);
    }
    previousFilterKeyRef.current = filterKey;
  }, [filterKey]);

  // Filtros para a query
  const filtersForQuery = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    return cleanFilters({
      ...baseFilters,
      date_start: moment(baseFilters.date_start).startOf('month').format('YYYY-MM-DD'),
      page: String(currentPage),
      size: baseFilters.size || '20',
    });
  }, [filters, currentPage]);

  const kpiFiltersObject = useMemo(
    () =>
      cleanFilters({
        date_start: moment(Object.fromEntries([...Array.from(filters)]).date_start)
          .startOf('month')
          .format('YYYY-MM-DD'),
      }),
    [filters],
  );

  const { data: dataAuditorias, isFetching } = useQuery<IData<IAuditoriaFiscal[]>>({
    queryKey: ['getAuditorias', filtersForQuery],
    queryFn: getAuditorias,
  });

  const { data: kpiData, isFetching: isFetchingKpi } = useQuery<
    IDataNoPagination<IAuditoriaFiscalKpi>
  >({
    queryKey: ['getAuditoriasKpi', kpiFiltersObject],
    queryFn: getAuditoriasKpi,
  });

  const auditorias = dataAuditorias?.data?.items ?? [];
  const total = dataAuditorias?.data?.total ?? 0;
  const kpi = kpiData?.data ?? { total: 0, ok: 0, erro: 0, fatura: 0, sem_pasta: 0 };

  // Acumular dados quando nova página é carregada
  useEffect(() => {
    if (isFetching) return;

    isLoadingMoreRef.current = false;

    if (auditorias && auditorias.length > 0) {
      if (currentPage === 1) {
        setAllAuditorias(auditorias);
      } else {
        setAllAuditorias(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const newItems = auditorias.filter(item => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
      const pageSize = Number(filters.get('size')) || 20;
      const totalPages = Math.ceil(total / pageSize);
      setHasMore(currentPage < totalPages);
    } else if (auditorias && auditorias.length === 0 && currentPage === 1) {
      setAllAuditorias([]);
      setHasMore(false);
    }
  }, [auditorias, currentPage, total, filters, isFetching]);

  // Callback para carregar mais
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, isFetching]);

  // Intersection Observer para infinite scroll
  useEffect(() => {
    if (!hasMore || isFetching) return undefined;

    let observer: IntersectionObserver | null = null;

    const timeoutId = setTimeout(() => {
      const element = loadMoreRef.current;
      if (!element) return;

      observer = new IntersectionObserver(
        entries => {
          if (!entries[0].isIntersecting) return;
          if (!hasMore || isFetching || isLoadingMoreRef.current) return;

          if (observer) {
            observer.disconnect();
            observer = null;
          }

          isLoadingMoreRef.current = true;
          loadMore();
        },
        { threshold: 0.1, rootMargin: '100px' },
      );

      observer.observe(element);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      if (observer) observer.disconnect();
    };
  }, [hasMore, isFetching, loadMore]);

  return (
    <PageBox>
      <div className="flex flex-col gap-2">
        {/* Header */}
        <div className="flex items-center justify-between pb-5">
          <div className="flex content-start lg:content-center">
            <ReturnButton previousPage="modulesHome/supervisor" />
            <div className="h-full">
              <div
                className="flex items-center justify-center rounded-full p-3"
                style={{ backgroundColor: '#FFEBEE' }}
              >
                <MagnifyingGlass color="#E53935" className="size-8" />
              </div>
            </div>
            <div className="flex w-full flex-col justify-center gap-1 pl-2">
              <Text fontWeight="bold" fontSize="16px">
                AGENTE DE AUDITORIA LUCRO PRESUMIDO
              </Text>
              <Text fontSize="12px" color="gray.500">
                Resultado da auditoria automatizada de impostos (PIS, COFINS, Retencao)
              </Text>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <SummaryCards
          kpi={kpi}
          isLoading={isFetchingKpi}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilter}
        />

        {/* Filters */}
        <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
          <Flex align="flex-end" gap={3} flexWrap="wrap">
            <Box flex={1} minW="300px">
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                BUSCAR
              </Text>
              <HStack spacing={2}>
                <SearchModeBar
                  modes={MODES_ID_NOME_CNPJ}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as 'Nome' | 'ID' | 'CNPJ')}
                  value={searchText}
                  onValueChange={v => {
                    setSearchText(v);
                    debouncedSearch(v);
                  }}
                />
              </HStack>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                MES DE REFERENCIA
              </Text>
              <MonthNavigator
                currentDate={currentFilterDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
            </Box>
          </Flex>
        </Box>

        {/* Cards Grid */}
        {isFetching && currentPage === 1 && (
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        {!isFetching && allAuditorias.length === 0 && (
          <Flex justify="center" align="center" h="400px" direction="column" gap={4}>
            <Warning size={48} color="#9CA3AF" />
            <Text color="gray.500">Nenhum resultado encontrado para este periodo</Text>
          </Flex>
        )}

        {allAuditorias.length > 0 && (
          <>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {allAuditorias.map(auditoria => (
                <AuditoriaCard key={auditoria.id} auditoria={auditoria} />
              ))}
            </SimpleGrid>

            {/* Load more trigger */}
            <Box ref={loadMoreRef} py={4}>
              {isFetching && currentPage > 1 && (
                <Flex justify="center">
                  <Spinner size="md" color="blue.500" />
                </Flex>
              )}
              {!hasMore && allAuditorias.length > 0 && (
                <Text textAlign="center" color="gray.500" fontSize="sm">
                  Todas as {allAuditorias.length} auditorias carregadas
                </Text>
              )}
            </Box>
          </>
        )}
      </div>
    </PageBox>
  );
}
